import { CardContent, CardShell } from '@/components/cards/common';
import NoDataMessage from '@/components/states/NoDataMessage';
import MapChart from '@/routes/AudiencePage/charts/MapChart/index';
import { LocationPlaysResponse, SelectedPlatform } from '@/types/api/location';
import useFetchQuery from '@/hooks/useFetchQuery';
import { useEffect, useMemo, useState } from 'react';
import { useLocationPlaysQuery } from '@/services/revenueApi';
import { mixpanelTrack, RevenueDashboardWidget } from '@/utils';
import { MIXPANEL_EVENT } from '@/utils/mixpanelUtils/events';
import { getFirstDisplayPlatform } from '@/utils/platformUtils';
import LocationModal from './LocationModal';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { AUDIENCE_TAB_EXPERIMENTAL_FEATURES_FLAG } from '@/utils/constants';
import { Button, Tooltip } from '@acast-tech/decibel';
import { ChevronRightRegularGlyph } from '@acast-tech/decibel-glyphs';

const LocationCard = ({ className }: { className?: string }) => {
  const { queryResult } = useFetchQuery(useLocationPlaysQuery, undefined, { sources: [] });

  const { data: locationPlays } = queryResult;

  const platforms = useMemo(() => {
    return [...new Set(locationPlays?.plays?.map((play) => play.platform) ?? [])];
  }, [locationPlays]);

  type SelectedPlatform = (typeof platforms)[number];

  const [selectedPlatform, setSelectedPlatform] = useState<SelectedPlatform | null>(
    platforms?.[0] ?? null
  );

  const handlePlatformChange = (platform: SelectedPlatform) => {
    setSelectedPlatform(platform);

    mixpanelTrack(MIXPANEL_EVENT.audience.demographics.platformRadioSelected, {
      page: 'audience',
      widget: RevenueDashboardWidget.LocationDemographics,
      platform: platform,
      demographics: 'location',
    });
  };

  useEffect(() => {
    setSelectedPlatform(
      getFirstDisplayPlatform({ plays: platforms.map((p) => ({ platform: p })) })
    );
  }, [platforms]);

  return (
    <CardShell className={className} title="Top locations">
      <CardContent
        {...queryResult}
        renderData={(locationData: LocationPlaysResponse) => (
          <Data
            data={locationData}
            selectedPlatform={selectedPlatform}
            handlePlatformChange={handlePlatformChange}
          />
        )}
      />
    </CardShell>
  );
};

const Data = ({
  data,
  selectedPlatform,
  handlePlatformChange,
}: {
  data: LocationPlaysResponse;
  selectedPlatform: SelectedPlatform<typeof data> | null;
  handlePlatformChange: (platform: SelectedPlatform<typeof data>) => void;
}) => {
  const ldClient = useLDClient();
  const audienceTabExperimentalFeaturesEnabled =
    ldClient?.variation(AUDIENCE_TAB_EXPERIMENTAL_FEATURES_FLAG, false) ?? false;

  if (data?.plays?.length < 1 || selectedPlatform === null) {
    return <NoDataMessage dataType="location data" />;
  }

  return (
    <div className="mt-6 flex flex-col gap-4 justify-between">
      <MapChart
        data={data}
        selectedPlatform={selectedPlatform}
        handlePlatformChange={handlePlatformChange}
      />
      {audienceTabExperimentalFeaturesEnabled ? (
        <LocationModal />
      ) : (
        <div className="flex flex-row justify-end">
          <Tooltip title="Coming soon" placement="bottom-end">
            <span>
              <Button
                className="self-right"
                variant="tertiary"
                disabled={true}
                glyphEnd={ChevronRightRegularGlyph}
              >
                All countries
              </Button>
            </span>
          </Tooltip>
        </div>
      )}
    </div>
  );
};

export default LocationCard;
